import { SyncLoggingService } from '../../logging';
import { each, extend, filter, first, forEach, get, includes, intersection, isArray, isNil, keys, map } from 'lodash';
import moment from 'moment';
import { from, Observable } from 'rxjs';
import { DatabaseModel, DatabaseRecord, DatabaseRecordIdentifier } from '../models/database-record.class';

export interface DatabaseServiceOptions {
  version: number;
  dbName: string;
  objectStores: any;
  seed?: any;
  connected?: (service: DatabaseService) => Observable<void>;
  upgraded?: (service: DatabaseService) => Observable<void>;
}

export interface DatabaseQuery {
  [key: string]: string | number | boolean | Date | moment.Moment | DatabaseRecord;
}

export class DatabaseService {
  public options: DatabaseServiceOptions;

  private db: IDBDatabase;
  private connecting = false;

  constructor(public syncLog: SyncLoggingService) {}

  public connect() {
    if (this.connecting) {
      return new Promise(resolve => {
        setTimeout(resolve, 1000);
      });
    }

    return new Promise<void>((resolve, reject) => {
      if (this.db) {
        // if we're already connected, just resolve
        return resolve();
      }

      this.connecting = true;

      const request = window.indexedDB.open(this.options.dbName, this.options.version);

      request.onsuccess = event => {
        this.syncLog.trace(`Connected to ${this.options.dbName} version ${this.options.version}.`);
        this.db = (event.target as any).result;
        this.db.onerror = this.onDatabaseError.bind(this);
        this.connecting = false;

        this.seedDatabase().then(() => {
          if (this.options.connected) {
            this.options.connected(this).subscribe(() => resolve());
          } else {
            resolve();
          }
        });
      };

      request.onerror = event => {
        this.syncLog.trace(`Failed to connect to ${this.options.dbName} version ${this.options.version}.`);
        reject(event);
      };

      request.onupgradeneeded = event => {
        this.db = (event.target as any).result;
        this.syncLog.trace(`DB Upgrade: Upgrading database ${this.options.dbName}.`);
        if (this.options.upgraded) {
          this.options.upgraded(this).subscribe();
        }
      };
    });
  }

  public getVersion() {
    return this.db.version;
  }

  public get<T extends DatabaseRecord>(model: DatabaseModel = DatabaseRecord, where: DatabaseRecordIdentifier | DatabaseQuery): Promise<T> {
    if (typeof where === 'string') {
      return this.getByKey<T>(model, where);
    } else {
      return this.queryOne<T>(model, where);
    }
  }

  protected getByKey<T extends DatabaseRecord>(model: DatabaseModel, key: DatabaseRecordIdentifier): Promise<T> {
    const objectStoreName = model.objectStoreName;
    return new Promise((resolve, reject) => {
      this.syncLog.trace(`${objectStoreName}: Getting ${key} from object store…`);

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const request = objectStore.get(key);

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error getting ${key} from object store.`);
        reject(event);
      };

      request.onsuccess = event => {
        this.syncLog.trace(`${objectStoreName}: Got ${key} from object store.`);
        if (!isNil(event.target.result)) {
          const record = new model(event.target.result) as T;
          resolve(record);
        } else {
          resolve(null);
        }
      };
    });
  }

  protected queryOne<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Promise<T> {
    return this.query(model, where).then((records: T[]) => {
      const record = first(records);
      return record;
    });
  }

  public query<T extends DatabaseRecord>(model: DatabaseModel, where: DatabaseQuery): Promise<T[]> {
    return this.getAll<T>(model).then((records: T[]) => {
      const whereFilter = {};
      each(where, (value: any, key: string) => {
        if (value instanceof DatabaseRecord) {
          whereFilter[key] = value.key;
        } else {
          whereFilter[key] = value;
        }
      });
      records = filter(records, whereFilter) as T[];
      return records;
    }) as Promise<T[]>;
  }

  public getAll<T extends DatabaseRecord>(model: DatabaseModel): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const objectStoreName = model.objectStoreName;
      this.syncLog.trace(`${objectStoreName}: Getting all records from object store.`);

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);

      if (!objectStore) {
        const err = new Error('Object store does not exist: ' + objectStoreName);
        this.syncLog.error(err, `${objectStoreName}: Object store does not exist: ${objectStoreName}.`);
        reject(err);
        return;
      }

      let request;
      const timeStart = performance.now();

      try {
        request = objectStore.getAll();
      } catch (ex) {
        reject(ex);
        return;
      }

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error getting all records from object store.`);
        reject(event);
      };

      request.onsuccess = event => {
        const records = map(event.target.result, record => {
          return new model(record) as T;
        });

        const timeEnd = performance.now();
        this.syncLog.trace(
          `${objectStoreName}: Got ${records.length} record(s) from object store in ${Math.round(timeEnd - timeStart)} ms.`,
        );

        resolve(records);
      };
    });
  }

  /**
   * Insert records into the datastore.
   *
   * @param objectStoreName Object store name (table name).
   * @param records Database records to insert.
   * @param forceUpdate Set to true to force an update if the record already exists.
   */
  public insert(
    model: DatabaseModel,
    recordOrRecords: DatabaseRecord | DatabaseRecord[],
    forceUpdate?: boolean,
    filters?: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const self = this;
      const records = (isArray(recordOrRecords) ? recordOrRecords : [recordOrRecords]) as DatabaseRecord[];

      if (records.length === 0) {
        resolve();
        return;
      }

      // We do the reverse storage like our API
      if (filters && filters.options && filters.options.append) {
        forEach(records, (record, i) => {
          extend(record, filters.options.append);
        });
      }

      const objectStoreName = model.objectStoreName;
      this.syncLog.trace(`${objectStoreName}: Inserting ${records.length} into object store...`);
      const timeStart = performance.now();

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const promises = map(records, (record: DatabaseRecord, index: number) => {
        return new Promise<void>((resolveInsert, rejectInsert) => {
          const obj = record.toJSON();

          if (isNil(obj.key)) {
            this.syncLog.warn(`${objectStoreName}: Attempting to insert record at index ${index} without a key.`);
          }

          const request = forceUpdate ? objectStore.put(obj) : objectStore.add(obj);

          request.onerror = event => {
            self.syncLog.error(event, `${objectStoreName}: Error inserting record at index ${index}.`);
            rejectInsert(event);
          };

          request.onsuccess = event => {
            const key = event.target.result;
            const timeEnd = performance.now();
            self.syncLog.trace(`${objectStoreName}: ${key} inserted in ${Math.round(timeEnd - timeStart)} ms.`);
            resolveInsert();
          };
        });
      });

      Promise.all(promises).then(() => resolve(), reject);
    });
  }

  public update(record: DatabaseRecord) {
    return new Promise<void>((resolve, reject) => {
      const objectStoreName = (record.constructor as DatabaseModel).objectStoreName;
      this.syncLog.trace(`${objectStoreName}: Updating ${record.key} in object store…`);
      const timeStart = performance.now();

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const request = objectStore.put(record.toJSON());

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error updating ${record.key} in object store.`);
        reject(event);
      };

      request.onsuccess = event => {
        const timeEnd = performance.now();
        this.syncLog.trace(`${objectStoreName}: ${record.key} updated in ${Math.round(timeEnd - timeStart)} ms.`);
        resolve();
      };
    });
  }

  public delete(model: DatabaseModel, record: DatabaseRecord) {
    const objectStoreName = (record.constructor as DatabaseModel).objectStoreName;
    return this.deleteByKey(objectStoreName, record.key);
  }

  public deleteByKey(objectStoreName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.syncLog.trace(`${objectStoreName}: Deleting ${key} from object store…`);
      const timeStart = performance.now();

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const request = objectStore.delete(key);

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error deleting record from object store.`);
        reject(event);
      };

      request.onsuccess = event => {
        const timeEnd = performance.now();
        this.syncLog.trace(`${objectStoreName}: ${key} deleted from object store in ${Math.round(timeEnd - timeStart)} ms.`);
        resolve();
      };
    });
  }

  public count(objectStoreName: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.syncLog.trace(`${objectStoreName}: Counting records in object store.`);
      const timeStart = performance.now();

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const request = objectStore.count();

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error counting records in object store.`);

        reject(event);
      };

      request.onsuccess = function() {
        const timeEnd = performance.now();
        this.syncLog.trace(`${objectStoreName}: counted ${request.result} records in ${Math.round(timeEnd - timeStart)} ms.`);
        resolve(request.result);
      };
    });
  }

  public clear(model: DatabaseModel): Promise<void> {
    return new Promise((resolve, reject) => {
      const objectStoreName = model.objectStoreName;
      this.syncLog.trace(`${objectStoreName}: Clearing object store.`);
      const timeStart = performance.now();

      const transaction = this.transaction(objectStoreName, resolve, reject);
      const objectStore = transaction.objectStore(objectStoreName);
      const request = objectStore.clear();

      request.onerror = event => {
        this.syncLog.error(event, `${objectStoreName}: Error clearing object store.`);
        reject(event);
      };

      request.onsuccess = event => {
        const timeEnd = performance.now();
        this.syncLog.trace(`${objectStoreName}: object store cleared in ${Math.round(timeEnd - timeStart)} ms.`);
        resolve();
      };
    });
  }

  public getObjectStoresNames(): string[] {
    return this.db ? Array.from(this.db.objectStoreNames) : [];
  }

  public createObjectStore(objectStoreName: string, key: any): Observable<any> {
    return from(
      new Promise<void>((resolve, reject) => {
        if (!includes(this.getObjectStoresNames(), objectStoreName)) {
          this.syncLog.trace(`DB Upgrade: Creating object store ${objectStoreName}.`);
          const objectStore = this.db.createObjectStore(objectStoreName, key);
          objectStore.createIndex(key.keyPath, key.keyPath, { unique: true });
          resolve();
        } else {
          this.syncLog.trace(`DB Upgrade: Object store ${objectStoreName} already exists; skipping upgrade.`);
          resolve();
        }
      }),
    );
  }

  private transaction(objectStoreName, resolve, reject) {
    let transaction;

    try {
      transaction = this.db.transaction([objectStoreName], 'readwrite');
    } catch (err) {
      this.syncLog.error(err, `${objectStoreName}: Error executing transaction on object store.`);
      reject(err);
    }

    transaction.oncomplete = event => {
      // resolve()
    };

    transaction.onerror = event => {
      this.syncLog.error(event, `${objectStoreName}: Error executing transaction on object store: ${event.target.error}.`);
      reject(event);
    };

    return transaction;
  }

  /**
   * Exports contents of 'fitforce-planner' database in json format
   * @example { "fitforce-db": { "version": 9, "timestamp": "2019-01-14T21_42_27Z", "exercises": [...], "equipment: [...],", etc. } }
   */
  public exportToJSON(): Promise<any> {
    const promises = [];

    forEach(this.getObjectStoresNames(), (objectStoreName: string) => {
      const promise = new Promise((resolve, reject) => {
        const transaction = this.transaction(objectStoreName, resolve, reject);
        const objectStore = transaction.objectStore(objectStoreName);

        if (!objectStore) {
          reject(new Error('Object store does not exist: ' + objectStoreName));
          return;
        }

        const request = objectStore.getAll();

        request.onsuccess = event => {
          resolve({
            name: objectStoreName,
            values: event.target.result,
          });
        };

        request.onerror = event => {
          reject();
        };
      });

      promises.push(promise);
    });

    return Promise.all(promises).then(results => {
      const data = {};
      data[this.options.dbName] = {
        version: this.options.version,
        timestamp: moment()
          .utc()
          .format(),
        user: JSON.parse(localStorage.getItem('midas-user')),
        'db-last-synced': {},
      };

      forEach(results, result => {
        data[this.options.dbName][result.name] = result.values;
        data[this.options.dbName]['db-last-synced'][result.name] = localStorage.getItem(`db-${result.name}-last-synced`);
      });

      return data;
    });
  }

  /**
   * Clears the browser's fitforce-planner object stores that intersect with the import file, and inserts
   * the values of the properties into indexedbb
   * @param json an object containing data to import to indexeddb
   */
  public importFromJSON(json: any): Promise<any> {
    const fitforceDb = json[this.options.dbName];
    if (!fitforceDb) {
      throw new Error('Invalid database in Import File');
    }

    if (!fitforceDb.version || fitforceDb.version !== this.options.version) {
      throw new Error('Invalid version');
    }

    const promises = [];
    forEach(intersection(Object.keys(this.options.objectStores), Object.keys(fitforceDb)), objectStoreName => {
      const clearPromise: Promise<any> = new Promise((resolve, reject) => {
        return this.executeRequest(objectStoreName, objectStore => objectStore.clear(), resolve, reject);
      });

      const promise = clearPromise
        .then(() => {
          const inserts = [];
          fitforceDb[objectStoreName].forEach(value => {
            const insert = new Promise((resolve, reject) => {
              return this.executeRequest(objectStoreName, objectStore => objectStore.put(value), resolve, reject);
            });

            inserts.push(insert);
          });

          return Promise.all(inserts);
        })
        .catch(err => {
          throw err;
        });

      promises.push(promise);
    });

    return Promise.all(promises);
  }

  /**
   * Executes a transaction on an object store and resolves or rejects the provided promise
   * parameters on success or error
   * @param objectStoreName object store name
   * @param requestDelegate request to execute delegated by caller
   * @param resolve resolve param of promise
   * @param reject reject param of promise
   */
  private executeRequest(objectStoreName: string, requestDelegate: (objectStore) => any, resolve, reject) {
    const transaction: IDBTransaction = this.transaction(objectStoreName, resolve, reject);
    const objectStore: IDBObjectStore = transaction.objectStore(objectStoreName);

    if (!objectStore) {
      reject(new Error('Object store does not exist: ' + objectStoreName));
      return;
    }

    const request = requestDelegate(objectStore);

    request.onsuccess = event => {
      resolve();
    };

    request.onerror = event => {
      reject();
    };
  }

  private seedDatabase(): Promise<any> {
    if (this.options.seed) {
      const stores = keys(this.options.objectStores);

      const promises = filter(
        map(stores, name => {
          const records = this.options.seed[name];
          if (records && records.length > 0) {
            return this.insert({ objectStoreName: name } as DatabaseModel, records, true);
          }
        }),
      );

      return Promise.all(promises);
    } else {
      return new Promise<void>(resolve => resolve());
    }
  }

  private onDatabaseError(event) {
    const err = get(event, 'target.error', event);
    this.syncLog.error(err, `Database error.`);
    console.log('Database error:', err);
  }
}
